export default {
  overview: {
    title: "Supplier Overview",
    subtitle: "List of all suppliers.",
    archive_title: "Archived suppliers",
    actions: {
      create_new: "Create new supplier",
    },
    search: {
      placeholder: "e.g. name or town",
    },
    table: {
      headers: {
        name: "Supplier",
        contact: "Contact person",
      },
      more_contacts: "+{count} more",
    },
  },
  detail: {
    title: "Supplier",
    actions: {
      update: "Save",
    },
    tabs: {
      general: "General",
      contacts: "Contact persons",
      locations: "Locations",
      assortment: "Assortment",
      files: "Files",
      activity: "Activity",
    },
    form: {
      name: "Name",
      kind: "Kind",
      telephoneNumber: "Phone",
      email: "E-mail",
      website: "Website",
      customerNumber: "Our customer number",
      address_search_unavailable_hint: "Enter the address in the fields below.",
      sections: {
        base_data: "Master data",
        contact: "Contact",
        // The supplier's own address; further addresses are its locations.
        address: "Head office",
        conditions: "Terms",
        note: "Note",
      },
      contacts: {
        title: "Contact persons",
        subtitle: "Contact at the supplier",
        empty: "No contact persons yet.",
      },
    },
  },
  create: {
    title: "Create Supplier",
    subtitle: "Create a new supplier.",
  },
  kind: {
    manage: "Manage kinds",
    no_results: "No matching kind found.",
    overview: {
      title: "Kinds of supplier",
      subtitle: "What tells suppliers apart, e.g. building materials dealer or equipment rental.",
      empty_items: "No kinds yet.",
      add_item: "Add kind",
    },
  },
  // The suppliers' prices on machines and materials (docs/suppliers.md, "Prices"). Names and dates never travel as
  // parameters: the bundle escapes them; the components add them themselves.
  prices: {
    valid_from_window: "Only 2000 to 2100",
    // "Gilt ab heute · anderes Datum": the day a new price holds from, a field only on request.
    valid_from_today: "Valid from today",
    valid_from_other: "other date",
    valid_from: "Valid from",
    // A price cleared from a day on: a state, said rather than dashed out.
    no_price: "no price",
    // "Change price" on a row of a machine's or material's purchase tab.
    change: {
      title: "Change price",
      action: "Change price",
      submit: "Save",
      currency: "Currency",
      price: "New price",
      // Before the current price and its date, appended in code: "Until now 13.80 EUR / t".
      before: "Until now",
      none_before: "No price in this currency until now",
      // Before the date and price of a later price already set: "Already set from 01/12/2026: 14.50 EUR".
      upcoming: "Already set from",
    },
    add: {
      title: "Add price",
      submit: "Add",
      supplier: "Supplier",
      // Under the field once a typed name is picked as a new supplier.
      new_supplier: "New supplier, created with the price",
      qualifier: "Qualifier",
      currency: "Currency",
      price: "Price",
      article_number: "Supplier's article number",
      // Machines: the unit the supplier bills this row in (hour, day, week, month).
      unit: "Billed per",
    },
    // "Edit row" in a row's menu (purchase tab, assortment): qualifier, article number, a machine's unit.
    edit_row: {
      action: "Edit row",
      title: "Edit row",
      submit: "Save",
      // Under "Billed per" once another unit is picked; {unit} is its name ("Week").
      unit_changed: "Every price of the row then counts per {unit}. Booked reports stay as they are until “Update”.",
    },
    // Several machines or materials into the Sortiment at once, each with its first price.
    add_items: {
      title_material: "Add materials",
      title_machine: "Add machines",
      pick_material: "Search material",
      pick_machine: "Search machine",
      none_material: "No material chosen yet",
      none_machine: "No machine chosen yet",
      submit_count: "Add {count}",
    },
    // A supplier with a price for the item already (Assortment: "Add material"; Purchasing: "Add price"): under its
    // name in the search, under the row the qualifiers it has already ("Already in the assortment: without qualifier,
    // “Big Bag”."; put together in code).
    taken: {
      in_assortment: "Already in the assortment",
      without_qualifier: "without qualifier",
      // A qualifier in the list: “Big Bag”.
      quoted: "“{qualifier}”",
      second_price: "For a second price, give another qualifier. Only a new price? “Change price” on its row.",
      qualifier_needed: "Qualifier needed",
      qualifier_taken: "Already exists",
    },
    // Under the purchase price when a material or machine comes into a project: whose price it is.
    source: {
      standard: "default",
      project: "for this project",
      change: "Change",
      change_label: "Supplier for this project",
      // The line under the selling price (project, customer), completed in code: "Bought from Betonwerk Donautal · Change".
      bought_at: "Bought from",
      none: "No supplier yet",
      // Without the right to change the choice: opens the same list to look at.
      all: "All suppliers",
    },
    overview: {
      typical: "Usually",
      range: "{from} – {to} from 1 supplier | {from} – {to} across {count} suppliers",
      suppliers: "Suppliers",
      add: "Add price",
      empty: "No supplier yet.",
      make_default: "Use as default",
      upcoming: "from",
      also: "also {prices}",
      archived: "archived",
      history: "History",
      open_supplier: "Open supplier",
      // The "Abstand" of the default itself (what the others are measured against), and of a row that shares no
      // currency with it.
      deviation_base: "Baseline",
      deviation_other_currency: "other currency",
      // A machine row that does not convert into the default's unit (hour against day/week/month).
      deviation_other_unit: "other unit",
      // Before a unit, appended in code: "−17 % (per day)".
      per: "per",
      // An example quantity, nothing stored: fills the column "Delivered at 10 t".
      quantity: "Compare at",
      effective: "Delivered",
      // A material's, beside the quantity to compare at: delivered (with freight) or fetched (none), then "Ex yard".
      delivery: "Delivery",
      pickup: "Pickup",
      effective_pickup: "Ex yard",
      at_quantity: "at",
      // After "at" and "from" a count of a machine unit, appended in code: "at 12 days".
      unit_count: {
        HOUR: "hour | hours",
        DAY: "day | days",
        WEEK: "week | weeks",
        MONTH: "month | months",
      },
      tiers_from: "Tiered from",
      discount: "−{percent}\u00a0% discount",
      freight: "+\u00a0{amount} freight",
      freight_unknown: "Freight in another currency only",
      cash_discount: "−{percent}\u00a0% early payment",
      // A rented machine: no freight, the supplier's transport tariff comes on top.
      plus_transport: "plus transport",
      columns: {
        supplier: "Supplier",
        price: "Price",
        deviation: "Difference",
      },
    },
    revisions: {
      title: "Price history",
      valid_from: "Valid from",
      price: "Price",
      recorded: "Recorded",
      tiers: "Tier prices",
      upcoming: "still to come",
      sources: {
        SETUP: "At setup",
        AUDIT_BACKFILL: "From the activity log",
        SORTIMENT: "In the assortment",
        DIALOG: "When added",
        IMPORT: "Price list imported",
        RECORD_EDIT: "Changed in the master data",
      },
      remove: {
        title: "Remove price?",
        text: "The entry leaves the history. If it was the price in force, the one before it applies again.",
      },
      // On the greyed-out 🗑 of the only price: why it stays.
      only_price_row: "The only price: remove the row instead",
      only_price_accessory: "The only price: remove the accessory instead",
    },
    kind: {
      MATERIAL: "Material",
      MACHINE: "Machine",
    },
    tiers: {
      title: "Quantity prices",
      from: "From quantity",
      price: "Price",
      add: "Add quantity price",
      positive: "Greater than 0",
      increasing: "Greater than the tier before",
      from_short: "from",
    },
    price_list: {
      title: "Change prices",
      valid_from: "New prices valid from",
      currency: "Currency",
      partial: "The list could not be loaded completely.",
      more: "1 more entry – reach it through the filter | {n} more entries – reach them through the filter",
      current: "Current",
      new: "New",
      changed: "No change | 1 price changed | {count} prices changed",
      submit: "Apply",
    },
    assortment: {
      title: "Assortment",
      materials: "Materials",
      machines: "Machines",
      filter_materials: "Filter materials",
      filter_machines: "Filter machines",
      price_list: "Change prices",
      add_material: "Add material",
      add_machine: "Add machine",
      open_machine: "Open machine",
      open_material: "Open material",
      default: "Default supplier",
      article: "Art. no.",
      empty: "This supplier delivers nothing here yet.",
      no_match: "Nothing found.",
      not_set_up: "The assortment exists once purchase prices are assigned to suppliers.",
      columns: {
        price: "Price",
      },
      remove: {
        action: "Remove from assortment",
        title: "Remove supplier",
        text: "This supplier's price is removed. Projects keep their copy, the price history stays.",
        agree: "Remove",
      },
    },
  },
  // Project prices (Objektpreise): a supplier's special price for one project, with "valid from" (docs/suppliers.md).
  // The components append names, dates and prices themselves, never as parameters.
  object_prices: {
    // On the project after a row's price: "102.46 EUR / m³ Project price ✎".
    marker: "Project price",
    // Before an Objektpreis's day, completed in code: "from 09/09/2026".
    valid_from: "from",
    add: "Add project price",
    edit: "Edit project price",
    remove: "Remove",
    dialog: {
      title_add: "Add project price",
      title_edit: "Edit project price",
      submit_add: "Add",
      submit_edit: "Save",
      supplier: "Supplier",
      qualifier: "Qualifier (optional)",
      price: "Price",
      note: "Offer or project number (optional)",
      // Under the price, completed in code: "Supplier today 918.00 EUR / t".
      supplier_today: "Supplier today",
      // When editing, if a further one is set for later, completed in code:
      // "Then from 11/01/2026: 117.80 EUR / m³ · edit".
      next: "Then",
      edit_next: "edit",
      remove_title: "Remove project price?",
      remove_text: "Booked reports keep their price. New ones book the supplier's price.",
    },
    // On the Einkauf tab and in the assortment under a row: "Project prices: Kita Regensburg 860.00 EUR, Wohnanlage
    // Süd 845.00 EUR + 2".
    in_projects: "Project price: | Project prices:",
    more: "+ {n}",
    // What "+ 2" opens: every project price of the row, per project.
    all: {
      title: "Project prices",
      // In the ⋮ of a row with project prices (Einkauf, Sortiment).
      action: "Project prices",
      project: "Project",
    },
  },
  // Rentals (docs/suppliers.md, "Mieten"): delivery to pick-up of a rented device, on the project's Machines tab and
  // at a report's machine line. Dates, names and amounts are appended in code.
  // Accessories at a machine's supplier row (docs/suppliers.md, "Zubehör"): each a name and dated prices, every one in
  // its own unit. Names, dates and prices are appended by the components, never passed as parameters.
  accessories: {
    // In the ⋮ of a machine's supplier row; a compact row each below it.
    add: "Add accessory",
    // In front of the unit, appended in code: "per day".
    per: "per",
    // A column of an accessory's price history.
    columns: {
      unit: "Billing",
    },
    // On removing a supplier's last row on a machine, after the confirmation's text.
    remove_with_row: "It is its last row on this machine: its accessories for it are removed along with it.",
    remove: {
      action: "Remove",
      title: "Remove accessory",
      text: "Rentals that take it along keep its name and price.",
      agree: "Remove",
    },
    dialog: {
      title_add: "Add accessory",
      // Name and price in one dialog, from the row's ✎ and ⋮.
      title_edit: "Edit accessory",
      submit_add: "Add",
      submit_edit: "Save",
      name: "Name",
      // Over currency, billing, price and "valid from": the price applies from a day on, the name at once.
      price: "Price",
      name_length: "At most 120 characters",
    },
  },
  rentals: {
    title: "Rentals",
    add: "Add rental",
    empty: "No rental yet. Add one here, or at its start at the machine in a report.",
    edit: "Edit rental",
    // "running since 05.10."
    running_since: "running since",
    // Not delivered yet: "from 05.11."
    from: "from",
    // Still running, beside the "running" chip: "since 05.10."
    since: "since",
    running: "running",
    // On a running rental: its end, with the day and the trip back, in the report's small dialog.
    pick_up: "End rental",
    // The columns in a rental's head, as the machines' purchase and selling price above.
    period: "Period",
    cost: "Cost",
    // Unfolded: what the cost is made of.
    breakdown: {
      rent: "Rent",
      note: "Note",
    },
    // After the rent price where it is the project's object price.
    object_price: "Object price",
    // Quantity billed, without the unit in front: "2 started weeks", "5 days", "12.5 hours"
    quantity: {
      DAY: "1 day | {n} days",
      WEEK: "1 started week | {n} started weeks",
      MONTH: "1 started month | {n} started months",
      HOUR: "1 hour | {n} hours",
    },
    // A location the supplier no longer has: "Mietpark Nord (location removed)"
    location_removed: "Location removed",
    // The supplier's trips: it brings the device at the start and fetches it at the end.
    kinds: {
      DELIVERY: "Delivery",
      PICKUP: "Pick-up",
    },
    // The same trips when the company drives itself: no "Delivery ourselves".
    self_kinds: {
      DELIVERY: "Fetched ourselves",
      PICKUP: "Returned ourselves",
    },
    // The rental's days without usage, as chips: "Sun 25.10. · 10 %", "Sat 24.10. · free", "Sun 01.11. · Allerheiligen · 10 %".
    idle: {
      label: "Without usage",
      // In the cost day popover: "Rent · without usage 10 % · Mietpark Nord"
      short: "without usage",
      title: "Days without usage",
      free: "free",
      // A public holiday the answer gives no name for.
      holiday: "Public holiday",
      // Unfolds the remaining chips of a long rental: "+5".
      more: "+{n}",
      less: "less",
      // In the dialog under the chips: "Total 836.00 EUR · Public holidays: Bavaria"
      total: "Total",
      region: "Public holidays",
      nationwide: "nationwide",
      // In the cost view: "4 days without usage · 836.00 EUR"
      count: "1 day without usage | {n} days without usage",
      regions: {
        BW: "Baden-Württemberg",
        BY: "Bavaria",
        BE: "Berlin",
        BB: "Brandenburg",
        HB: "Bremen",
        HH: "Hamburg",
        HE: "Hesse",
        MV: "Mecklenburg-Western Pomerania",
        NI: "Lower Saxony",
        NW: "North Rhine-Westphalia",
        RP: "Rhineland-Palatinate",
        SL: "Saarland",
        SN: "Saxony",
        ST: "Saxony-Anhalt",
        SH: "Schleswig-Holstein",
        TH: "Thuringia",
      },
    },
    // "Delivery 6 × 15 min", "Pick-up ourselves", "Delivery 1 trip"; without known minutes "6 time units".
    time_units: "time unit | time units",
    trip: "1 trip",
    self: "ourselves",
    gaps: {
      NO_PRICE: "No price",
      NO_WEIGHT: "Transport weight missing",
      NO_TARIFF: "No tariff",
      NO_CLASS: "No weight class",
      NO_CURRENCY: "Tariff without a price in this currency",
      NO_TIME_UNITS: "Duration missing",
    },
    // Accessories on a rental (a bucket, a breaker): "Accessories: Bucket 60 cm × 1 · 150.00 EUR".
    accessories: {
      title: "Accessories",
      add: "Add accessory",
      pick: "Accessory",
      none: "No accessory found",
      // At the end of the picker: a new one of the rental's supplier for the machine, with the name typed.
      create_new: "New accessory …",
      create_named: "Add “{name}” as an accessory …",
      create_at: "at the rental's supplier",
      quantity: "Quantity",
      remove: "Remove accessory",
      // One the supplier no longer keeps: the rental keeps its name and price.
      removed: "removed at the supplier",
      quantity_range: "Above 0 up to 9,999, at most two decimals",
      // In the cost history: "Rent 2,500.00 EUR · Accessories 150.00 EUR · Transport 288.75 EUR"
      cost: "Accessories",
      // In the activity log: "Accessory quantity: 2"
      audit: "Accessory",
    },
    dialog: {
      title_add: "Add rental",
      title_edit: "Edit rental",
      submit_add: "Add",
      submit_edit: "Save",
      machine: "Machine",
      device: "Device",
      row: "Purchase price",
      row_hint: "At which price is it rented?",
      location: "Location",
      head_office: "Head office",
      // A rental's start and end, whoever drives: the supplier ("by supplier") or the company ("ourselves").
      delivery: "Rental start",
      pickup: "Rental end (optional)",
      end: "Rental end",
      date: "Date",
      by_supplier: "by supplier",
      self: "ourselves",
      // How many time units the trip costs, with "× 15 min" behind it.
      time_units: "Duration",
      time_units_range: "0 to 999, at most two decimals",
      note: "Note (rental contract, delivery note)",
      // Under the row: "Price 1,250.00 EUR / wk as of 05.10.2026 · Re-read price"
      price: "Price",
      price_of: "as of",
      object_price: "Project price",
      reprice: "Re-read price",
      repricing: "Reading …",
      before_delivery: "Before the rental start",
      no_machines: "The project has no machine yet.",
      // Also where its only one is the own company's: the own stock is not rented.
      no_row: "The machine has no supplier's purchase price.",
      remove: "Remove",
      remove_title: "Remove rental?",
      remove_text: "Its costs go away; the reports cost the device themselves again.",
    },
    // In a rental's activity log.
    audit: {
      running: "still running",
      note: "Note",
      gap: "gap",
      basis: "tariff",
      weight: "transport weight",
      days_per_unit: "Rental days per unit",
      // After "Transport": the tariff the price comes from (its valid-from) and the day it was read on.
      tariff_from: "Tariff valid from",
      price_date: "Price as of",
      // "Without usage Sunday and holiday: 10 %"
      idle: "Without usage",
      source: "Price",
      mode: "Price from",
      sources: {
        BOOKED: "read at the rental start",
        REPRICED: "re-read",
      },
    },
    // At a report's machine line.
    report: {
      delivery: "Rental start",
      in_rental: "Rental",
      since: "since",
      pick_up: "End rental",
      title_delivery: "Rental start",
      title_pickup: "Rental end",
      submit_delivery: "Start rental",
      // On an Anlieferung from a report: which supplier (which row) the device is rented from.
      rented_from: "Rented from",
      submit_pickup: "End rental",
      // "The rental starts on 05.10.2026."
      starts_on: "The rental starts on",
      ends_on: "The rental ends on",
      saved_at_once: "This is saved right away, the report only with “Save”.",
    },
  },
  // Bookings of rows that follow the supplier, still at an older price (docs/suppliers.md, "Umstellen"). The component
  // appends dates and prices itself.
  following: {
    bookings: "1 booking | {n} bookings",
    projects: "in 1 project | in {n} projects",
    since: "since",
    still_at: "still at",
    still_older: "still at older prices",
    // What "Update" does to the purchase cost, as the confirmation's total; amount appended: "Purchase cost + 13.50 EUR".
    cost_change: "Purchase cost",
    unreachable: "1 more project without permission | {n} more projects without permission",
    // Opens the confirmation; only its "Update" writes.
    switch: "Update to their day's prices …",
    // On a project's purchase line: this material or machine there only, at once.
    switch_item: "Update",
    switching: "Updating …",
    // The confirmation: per project the bookings and their purchase cost before and after. Amounts appended in code.
    dialog: {
      title: "Update bookings",
      text: "The bookings get the purchase price that applies on their day. Billed hourly-wage reports keep their prices; every report changed is in its activity log.",
      project: "Project",
      bookings: "Bookings",
      since: "from",
      before: "Purchase before",
      after: "Purchase after",
      total: "Total",
      not_switched: "is not updated | are not updated",
      submit: "Update",
    },
    done: "No booking updated | 1 booking updated | {n} bookings updated",
    skipped: "1 report skipped, it was just changed | {n} reports skipped, they were just changed",
  },
  conditions: {
    discount: {
      title: "Discount",
      info: "Lowers every booked price of this supplier, tier prices too. A change applies from today; bookings of earlier days keep their discount.",
      valid_from: "Valid from",
      percent: "Discount",
      add: "Add discount",
      current: "valid today",
      upcoming: "upcoming",
      past: "replaced",
      same_day: "There is already a discount for this day",
      // Opens the dated list (a discount from a later day, or back-dated): the field itself holds from today.
      dated: "from another date …",
    },
    cash_discount: {
      title: "Early payment discount",
      percent: "Discount",
      days: "When paid within",
      days_suffix: "days",
    },
    freight: {
      title: "Freight",
      currency: "Currency",
      per_delivery: "Cost per delivery",
      free_from: "Free from order value",
      note: "Freight note",
    },
    // Rental days per week and month: convert prices per week or month into days. Empty means 5 and 20.
    rental: {
      title: "Rental",
      info: "Converts prices per week or month into days: in comparisons and for booked days of use (2 of 5 rental days = 0.4 weeks). The rental days also decide how many started weeks or months a rental costs.",
      week: "Week",
      month: "Month",
      days_suffix: "rental days",
      // After "Week = 5 rental days, Month = 20 rental days"; opens the dialog.
      change: "change",
      days_title: "Rental days",
      week_range: "1 to 7 rental days",
      month_range: "1 to 31 rental days",
      // Brings up Rental and Transport at a supplier without machines in its assortment and without terms of its own.
      open: "Rental and transport",
      // What a rental day without usage of the device costs, in percent of the day rate
      // (docs/suppliers.md, "Tage ohne Einsatz").
      idle: {
        title: "Days without usage",
        info: "Share of the day rate a rental day without usage costs (8 h on an hourly price). The first and last rental day count in full, public holidays follow the site's federal state.",
        weekdayPercent: "Weekday",
        saturdayPercent: "Saturday",
        sundayHolidayPercent: "Sunday and holiday",
        range: "0 to 100 %",
      },
    },
    // At Skonto, for Skonto and freight both.
    not_booked:
      "Skonto and freight are never booked. They count only in a machine's or material's Purchasing once a quantity is entered there: it then shows the delivered price.",
    percent_range: "From 0 to below 100 %",
    days_range: "0 to 365 days",
  },
  // Locations: further addresses of the supplier with their own contacts (docs/suppliers.md, "Standorte").
  locations: {
    title: "Locations",
    info: "Further addresses of the supplier, such as branches or depots, with their own contacts. They share its prices; a branch with prices of its own is entered as a supplier of its own.",
    // After kind and place in the overview and the picker: "Equipment rental · 12355 Musterstadt · +1 location".
    more: "+1 location | +{n} locations",
    empty: "No locations yet.",
    // As the contacts' "Add" in the tab beside it.
    add: "Add",
    add_title: "Add location",
    edit_title: "Edit location",
    // Without the right to edit: read only.
    view_title: "Location",
    add_submit: "Add",
    edit_submit: "Apply",
    // In a location's dialog, above its contacts.
    add_contact: "Add contact",
    name: "Name",
    name_taken: "Another location already has this name",
    address: "Address",
    email: "Dispatch e-mail",
    note_length: "At most {max} characters",
    // Under the contact dialog's title, the location's name appended in code.
    contact_subtitle: "Contact at the location",
    columns: {
      name: "Location",
      email: "Dispatch",
    },
    remove: {
      title: "Remove location",
      text: "The location and its contacts are gone once you save.",
      agree: "Remove",
    },
  },
  // Transport tariff: delivering and picking up rented machines, by time or per trip, by weight class. Figures,
  // amounts and days are appended in code ("Transport up to 10 t: 26.25 EUR per 15 min").
  transport: {
    title: "Transport",
    info: "Delivery and pick-up of rented machines. The machine's transport weight picks the class; “up to 10 t” includes 10 t.",
    add: "Add tariff",
    remove: "Remove tariff",
    basis: {
      TIME: "by time",
      TRIP: "per trip",
    },
    per: {
      // Only without known minutes; else "per" and the minutes ("per 15 min").
      TIME: "per time unit",
      TRIP: "per trip",
    },
    // "per 15 min": the time unit a trip is billed in.
    per_time: "per",
    minutes: "min",
    // The field "Billed per [15] min", in the activity log with a tariff's number of classes.
    time_unit: "Billed per",
    classes: "Classes",
    columns: {
      weight: "Weight",
      price: "Price",
    },
    up_to: "up to",
    above: "above",
    every_weight: "any weight",
    add_class: "Add class",
    toll: "incl. toll",
    note: "Transport note",
    rules: {
      minutes: "1 to 240 minutes",
      weight: "Above 0 up to 1,000 t",
      ascending: "More than the class before",
    },
    // Under a row on a machine's purchase tab.
    line: {
      from: "from",
      over: "above",
      no_price: "no price above",
    },
  },
  archive: {
    title: "Archive supplier?",
    submit: "Archive",
    // What archiving means for its prices: the projects keep their copies, new projects get none.
    projects:
      "The prices are no longer offered to new projects. | The prices stay bookable in 1 running project and are no longer offered to new projects. | The prices stay bookable in {n} running projects and are no longer offered to new projects.",
    // The supplier's running rentals: they stay as they are.
    running_rentals: "1 running rental keeps counting. | {n} running rentals keep counting.",
    defaults: "Default for",
    new_default: "New default",
    stays_default: "stays the default – no other supplier",
    stays_default_no_right: "stays default (no right to change purchase prices)",
  },
  setup: {
    title: "Set up suppliers",
    subtitle: "From now on every purchase price belongs to a supplier. Assign the names used so far.",
    // After the setup, for prices written without a supplier since.
    subtitle_after: "Assign the names of the purchase prices without a supplier.",
    hint: "Set up suppliers",
    invitation_empty: "Supplier management is not set up yet. Assign your purchase prices to suppliers once.",
    invitation_leftovers: "1 purchase price has no supplier yet. | {n} purchase prices have no supplier yet.",
    labels: "Names used so far",
    unnamed: "Unnamed",
    no_labels: "No purchase prices to assign. “Set up” gets supplier management ready.",
    settled: "Every purchase price belongs to a supplier.",
    leftovers:
      "1 price without a supplier came in during the setup. Please assign it once more. | {n} prices without a supplier came in during the setup. Please assign them once more.",
    type_new: "Or type a new name",
    own_company: "Own company",
    new_prefix: "New:",
    columns: {
      label: "Name",
      used: "Used",
      supplier: "Becomes",
    },
    used: {
      machines: "1 machine | {n} machines",
      materials: "1 material | {n} materials",
      projects: "1 project price | {n} project prices",
      bookings: "1 booking | {n} bookings",
    },
    preview: {
      action: "Preview",
      new_suppliers: "No new supplier is created. | 1 supplier is created. | {count} suppliers are created.",
      collisions:
        "On 1 machine or material two prices end up with the same supplier; the former name becomes the qualifier: | On {n} machines and materials two prices end up with the same supplier; the former name becomes the qualifier:",
    },
    apply: "Set up",
    confirm: {
      title: "Set up suppliers?",
      text: "Afterwards every purchase price belongs to a supplier, and prices are maintained at the supplier.",
    },
    done: {
      title: "Suppliers set up",
      suppliers: "No supplier created. | 1 supplier created. | {n} suppliers created.",
      rows: "No price assigned. | 1 price assigned. | {n} prices assigned.",
      to_suppliers: "Go to suppliers",
    },
  },
  component: {
    autocomplete: {
      no_data: "No supplier “{value}” found.",
      // At the end of the list, with the name typed; created only with what the dialog adds.
      create: "Add “{name}” as a new supplier",
      create_later: "More details later on the supplier",
    },
  },
}
